import type { CatalogFacets } from '@sbg/shared/schemas';
import type { CatalogFilterState, ListFilterKey } from '@/lib/catalog';

// ── Price slider ────────────────────────────────────────────────────────────────────────────────
//
// Prices span two orders of magnitude or more (a per-sq-ft price next to a per-slab price), so the
// slider is logarithmic: equal distances on the track are equal *ratios*, which keeps both ends usable.

/** Number of positions on the track. */
export const SLIDER_STEPS = 1000;

/** The price step that feels natural at this magnitude (₹5 under 100 … ₹1000 from 1 lakh). */
export function niceStep(value: number): number {
  if (value < 100) return 5;
  if (value < 1_000) return 25;
  if (value < 10_000) return 100;
  if (value < 100_000) return 500;
  return 1_000;
}

export function roundToNice(value: number): number {
  const step = niceStep(value);
  return Math.round(value / step) * step;
}

/** Track position (0 … SLIDER_STEPS) of a rupee amount within `[min, max]`. */
export function priceToPosition(price: number, min: number, max: number): number {
  if (max <= min || min <= 0) return 0;
  const clamped = Math.min(max, Math.max(min, price));
  return Math.round((Math.log(clamped / min) / Math.log(max / min)) * SLIDER_STEPS);
}

/** Rupee amount at a track position, rounded to a natural step and kept inside `[min, max]`. */
export function positionToPrice(position: number, min: number, max: number): number {
  if (max <= min || min <= 0) return min;
  if (position <= 0) return min;
  if (position >= SLIDER_STEPS) return max;
  const raw = min * Math.pow(max / min, position / SLIDER_STEPS);
  return Math.min(max, Math.max(min, roundToNice(raw)));
}

/** The rupee bounds of the matching set, from the facets' minor-unit range. `null` = nothing to slide. */
export function priceBounds(
  facets: CatalogFacets | undefined,
): { min: number; max: number } | null {
  const minMinor = facets?.price?.minMinor;
  const maxMinor = facets?.price?.maxMinor;
  if (minMinor == null || maxMinor == null) return null;
  const min = Math.max(1, Math.floor(minMinor / 100));
  const max = Math.ceil(maxMinor / 100);
  return max > min ? { min, max } : null;
}

// ── Facet groups ────────────────────────────────────────────────────────────────────────────────

export interface FacetOption {
  value: string;
  label: string;
  count: number;
  selected: boolean;
  swatchHex?: string;
}

const isSwatch = (value: string | null): value is string =>
  value !== null && /^#[0-9a-f]{3}(?:[0-9a-f]{3})?$/i.test(value);

type RefKey = 'color' | 'finish' | 'origin' | 'application';

/**
 * The options of one list facet, in the API's order, each with its count and whether it is
 * selected. A selected value the API no longer lists (nothing matches it with the other filters)
 * is still shown — with a zero count — so it can always be switched off.
 */
export function refOptions(
  facets: CatalogFacets | undefined,
  filters: CatalogFilterState,
  key: RefKey,
): FacetOption[] {
  const selected = new Set<string>(filters[key] ?? []);
  const listed = facets?.[key] ?? [];
  const options: FacetOption[] = listed.map((item) => ({
    value: item.slug,
    label: item.label,
    count: item.count,
    selected: selected.has(item.slug),
    ...(isSwatch(item.swatchHex) ? { swatchHex: item.swatchHex } : {}),
  }));
  for (const value of selected) {
    if (!options.some((option) => option.value === value)) {
      options.push({ value, label: value, count: 0, selected: true });
    }
  }
  return options;
}

export function categoryOptions(
  facets: CatalogFacets | undefined,
  filters: CatalogFilterState,
): FacetOption[] {
  const options = (facets?.category ?? []).map((item) => ({
    value: item.slug,
    label: item.label,
    count: item.count,
    selected: filters.category === item.slug,
  }));
  if (filters.category && !options.some((option) => option.value === filters.category)) {
    options.push({ value: filters.category, label: filters.category, count: 0, selected: true });
  }
  return options;
}

export function thicknessOptions(
  facets: CatalogFacets | undefined,
  filters: CatalogFilterState,
): { value: number; count: number; selected: boolean }[] {
  const selected = new Set(filters.thickness ?? []);
  const options = (facets?.thickness ?? []).map((item) => ({
    value: item.value,
    count: item.count,
    selected: selected.has(item.value),
  }));
  for (const value of selected) {
    if (!options.some((option) => option.value === value)) {
      options.push({ value, count: 0, selected: true });
    }
  }
  return options.sort((a, b) => a.value - b.value);
}

export function availabilityOptions(
  facets: CatalogFacets | undefined,
  filters: CatalogFilterState,
): {
  value: NonNullable<CatalogFilterState['availability']>[number];
  count: number;
  selected: boolean;
}[] {
  const selected = new Set<string>(filters.availability ?? []);
  const options = (facets?.availability ?? []).map((item) => ({
    value: item.value,
    count: item.count,
    selected: selected.has(item.value),
  }));
  for (const value of filters.availability ?? []) {
    if (!options.some((option) => option.value === value)) {
      options.push({ value, count: 0, selected: true });
    }
  }
  return options;
}

// ── Active filter chips ─────────────────────────────────────────────────────────────────────────

export type FilterChipId =
  | { kind: 'category' }
  | { kind: 'list'; key: ListFilterKey; value: string }
  | { kind: 'thickness'; value: number }
  | { kind: 'price' }
  | { kind: 'featured' };

export const chipKey = (id: FilterChipId): string => {
  switch (id.kind) {
    case 'list':
      return `${id.key}:${id.value}`;
    case 'thickness':
      return `thickness:${String(id.value)}`;
    default:
      return id.kind;
  }
};
